import { useSyncExternalStore } from "react"
import { registerSW } from "virtual:pwa-register"
import { hasUnsavedWork } from "@/design-system/unsaved-work.ts"
import { isChunkLoadError } from "./chunk-error.ts"
import {
  atLaunch,
  checkDue,
  checkEvery,
  onTaken,
  reloadPressed,
  type CheckReason,
  type UpdateState,
} from "./update-policy.ts"

/*
 * How a tab learns of a new release of Beacon and moves onto it (`update-policy.ts` holds the
 * rules). `main.tsx` takes a release left waiting at launch, before the first paint, then starts
 * the rest: registering the service worker, looking for releases, and hearing when one is found,
 * when another tab takes one, and when a page's code a deploy replaced fails to load. Each surface
 * that offers the update (the notice, the avatar's drawer, the account menu, the palette, a page
 * that says Beacon has been updated) reads `useUpdate()` and presses `reloadToUpdate()`.
 *
 * Under `sst dev` there is no worker: `registerSW` does nothing and no release is ever found, but a
 * chunk that fails to load still says Beacon has been updated.
 */

/** Where this tab stands, and what of it the notice shows. */
export type Updates = {
  state: UpdateState
  /** The Member put the notice away (Later, or a swipe) for this state. */
  later: boolean
  /**
   * Something on screen offers the update in the notice's place, so the notice needn't: a page that
   * says Beacon has been updated, or the avatar's drawer, open with Update Beacon at its head.
   */
  saidElsewhere: boolean
}

let updates: Updates = { state: { status: "current" }, later: false, saidElsewhere: false }
/** How many of those say it now. */
let sayers = 0
const listeners = new Set<() => void>()

function set(next: Partial<Updates>) {
  updates = { ...updates, ...next }
  listeners.forEach((listener) => listener())
}

/** A new state, which brings the notice back however it was put away. */
const become = (state: UpdateState) => set({ state, later: false })

const subscribe = (listener: () => void) => {
  listeners.add(listener)
  return () => void listeners.delete(listener)
}

/** Where this tab stands with releases, for whatever offers the update. */
export function useUpdate() {
  return useSyncExternalStore(subscribe, () => updates)
}

/** Puts the notice away until the state changes; the drawer, the menu and the avatar keep it. */
export function putNoticeAway() {
  if (!updates.later) set({ later: true })
}

/**
 * Says, until the returned function is called, that something on screen offers the update in the
 * notice's place (`saidElsewhere`).
 */
export function sayElsewhere() {
  sayers += 1
  set({ saidElsewhere: true })
  return () => {
    sayers -= 1
    set({ saidElsewhere: sayers > 0 })
  }
}

/** The worker's registration, once `registerSW` hands it over. */
let registration: ServiceWorkerRegistration | undefined
/** Takes the waiting worker (`registerSW`'s own), once registering has started. */
let takeWaiting: (() => Promise<void>) | undefined
let lastCheckedAt: number | undefined
/** Reload was pressed in this tab, so the worker that takes it next was taken here. */
let takenHere = false

/** How long a release may take to move this tab onto it before the tab reloads anyway. */
const takesAtMost = 3000

/** Asks for `sw.js` afresh, when `reason` makes it due; a new release then installs and waits. */
function check(reason: CheckReason) {
  const visible = document.visibilityState === "visible"
  if (!registration || !checkDue({ reason, lastCheckedAt, now: Date.now(), visible })) return
  lastCheckedAt = Date.now()
  void registration.update().catch(() => undefined)
}

/**
 * Moves this tab onto the newest release, as Reload does wherever it is pressed: with unsaved work
 * it only says to save or discard it first; with a release waiting it takes it (every other tab
 * moves too, `onTaken`), and otherwise reloads the page.
 */
export function reloadToUpdate() {
  const waiting = Boolean(registration?.waiting)
  const does = reloadPressed({ unsaved: hasUnsavedWork(), waiting })
  if (does === "hold") {
    const { state } = updates
    become(
      state.status === "current"
        ? { status: "behind", because: "chunk", held: true }
        : { ...state, held: true },
    )
    return
  }
  if (does === "reload") return window.location.reload()
  takenHere = true
  void takeWaiting?.()
  // The worker takes over in moments; should it not, the page reloads on what it has, and the
  // next launch takes the release before its first paint.
  setTimeout(() => window.location.reload(), takesAtMost)
}

/**
 * A page's code didn't load: a release replaced it, most likely, so say so and look for it. Vite
 * reports it as it happens (`vite:preloadError`), and a page whose error `isChunkLoadError`
 * matches reports it too, for an import Vite's preloading didn't wrap; it counts once.
 */
export function chunkMissing() {
  // Offline, a chunk the cache doesn't hold fails too, and that is the connection, not a release.
  if (updates.state.status === "behind" || !navigator.onLine) return
  become({ status: "behind", because: "chunk", held: false })
  check("chunk")
}

const launchKey = "beacon:launch-took-update"

const readTried = () => {
  try {
    return sessionStorage.getItem(launchKey) !== null
  } catch {
    return false
  }
}

const markTried = (tried: boolean) => {
  try {
    if (tried) sessionStorage.setItem(launchKey, "1")
    else sessionStorage.removeItem(launchKey)
  } catch {
    // Without session storage a launch can't tell it already tried, so it doesn't.
  }
}

/** The browser's service workers, or none where it has none or won't say (site data blocked). */
function serviceWorkers() {
  try {
    return "serviceWorker" in navigator ? navigator.serviceWorker : undefined
  } catch {
    return undefined
  }
}

/**
 * At launch, before anything renders: takes a release found in an earlier session and left
 * waiting, and reloads onto it. A launch has nothing unsaved, and an installed app on a phone
 * mostly resumes rather than launching, so this is how it starts on the newest Beacon when it does.
 * Resolves at once when there is nothing to take, or when the browser won't say; otherwise never,
 * as the page reloads.
 */
export async function takeWaitingAtLaunch() {
  const workers = serviceWorkers()
  const controlled = Boolean(workers?.controller)
  const waiting = controlled
    ? await workers?.getRegistration().then(
        (registration) => registration?.waiting ?? undefined,
        () => undefined,
      )
    : undefined
  const launch = atLaunch({ controlled, waiting: Boolean(waiting), tried: readTried() })
  markTried(launch.tried)
  if (!workers || !waiting || launch.does !== "take") return
  const took = new Promise((resolve) =>
    workers.addEventListener("controllerchange", resolve, { once: true }),
  )
  // The worker (`sw.ts`) activates on this message.
  waiting.postMessage({ type: "SKIP_WAITING" })
  await Promise.race([took, new Promise((resolve) => setTimeout(resolve, takesAtMost))])
  window.location.reload()
  await new Promise(() => undefined)
}

/**
 * Registers the service worker and starts looking for releases: on coming back into view, every
 * half hour in view, on the network's return and after a chunk fails. A release found raises the
 * notice; a release another tab took reloads this one, or with unsaved work here says so instead.
 */
export function startUpdates() {
  window.addEventListener("vite:preloadError", (event) => {
    // Without `preventDefault`, the import still throws, so the page's error says so in place.
    if (isChunkLoadError(event.payload)) chunkMissing()
  })
  const workers = serviceWorkers()
  if (!workers) return
  let controlled = Boolean(workers.controller)

  workers.addEventListener("controllerchange", () => {
    const by = !controlled ? "first-install" : takenHere ? "here" : "elsewhere"
    controlled = true
    const does = onTaken({ by, unsaved: hasUnsavedWork() })
    if (does === "reload") window.location.reload()
    else if (does === "hold") become({ status: "behind", because: "other-tab", held: false })
  })

  takeWaiting = registerSW({
    immediate: true,
    onNeedRefresh: () => {
      if (updates.state.status === "current") become({ status: "ready", held: false })
    },
    // The plugin would reload as the new worker takes over; `controllerchange` above decides.
    onNeedReload: () => undefined,
    onRegisteredSW: (_url, registered) => {
      registration = registered
      lastCheckedAt = Date.now()
    },
  })

  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "visible") check("return")
  })
  window.addEventListener("online", () => check("online"))
  setInterval(() => check("interval"), checkEvery)
}
